import {
  CONTEXT_FIELD_SCHEMAS,
  EVENT_SCHEMAS,
  type FieldSpec,
} from "./schemas";

/**
 * Same denylist and stripping rules as emmi-observability's
 * packages/telemetry sanitize() (see schemas.ts's header comment): dropped
 * fields are never sent, only their count (`sdk_dropped_fields`). The server
 * (services/event-ingest) still validates as defense in depth — the SDK
 * strips, the server rejects.
 */
const PII_DENYLIST_PATTERN =
  /(email|name|username|phone|ip|address|token|password)/i;

function fieldPasses(spec: FieldSpec | undefined, value: unknown): boolean {
  if (!spec) return false;
  if (spec.type === "boolean") return typeof value === "boolean";
  if (spec.type === "integer") {
    return (
      typeof value === "number" &&
      Number.isInteger(value) &&
      (spec.minimum === undefined || value >= spec.minimum)
    );
  }
  if (spec.type === "string") {
    if (typeof value !== "string") return false;
    if (spec.enum) return (spec.enum as readonly string[]).includes(value);
    if (spec.pattern) return spec.pattern.test(value);
    return true;
  }
  return false;
}

function sanitizeObject(
  raw: Record<string, unknown>,
  fieldSchemas: Record<string, FieldSpec>,
): { sanitized: Record<string, unknown>; dropped: number } {
  const sanitized: Record<string, unknown> = {};
  let dropped = 0;

  for (const [key, value] of Object.entries(raw)) {
    if (key === "sdk_dropped_fields") continue;
    if (PII_DENYLIST_PATTERN.test(key) || !fieldPasses(fieldSchemas[key], value)) {
      dropped += 1;
      continue;
    }
    sanitized[key] = value;
  }

  return { sanitized, dropped };
}

export interface RawTelemetryEvent {
  eventName: string;
  properties: Record<string, unknown>;
  context?: Record<string, unknown>;
}

export interface SanitizedTelemetryEvent {
  eventName: string;
  eventVersion: number;
  anonymousAllowed: boolean;
  properties: Record<string, unknown>;
  context?: Record<string, unknown>;
}

/**
 * usage-event-pipeline/Shared Client SDK, telemetry-privacy/Payload
 * Allowlist: drops unknown properties/context keys, PII-denylisted keys, and
 * values that fail their own field's schema, before the event ever leaves
 * this device. Drops the whole event when a required field ends up missing
 * (a defensive addition over the published SDK: never send an event the
 * server would 400 on anyway) or when `event_name` has no matching schema.
 */
export function sanitize(
  raw: RawTelemetryEvent,
): SanitizedTelemetryEvent | undefined {
  const spec = EVENT_SCHEMAS[raw.eventName];
  if (!spec) {
    console.warn(`telemetry: dropping unregistered event ${raw.eventName}`);
    return undefined;
  }

  const { sanitized: properties, dropped: droppedProps } = sanitizeObject(
    raw.properties,
    spec.properties,
  );

  for (const key of spec.required) {
    if (!(key in properties)) {
      console.warn(
        `telemetry: dropping ${raw.eventName} — missing required field "${key}"`,
      );
      return undefined;
    }
  }

  let sdkDroppedFields = droppedProps;

  const result: SanitizedTelemetryEvent = {
    eventName: raw.eventName,
    eventVersion: spec.version,
    anonymousAllowed: spec.anonymousAllowed,
    properties,
  };

  if (raw.context) {
    const { sanitized: context, dropped: droppedContext } = sanitizeObject(
      raw.context,
      CONTEXT_FIELD_SCHEMAS,
    );
    sdkDroppedFields += droppedContext;
    result.context = context;
  }

  if (sdkDroppedFields > 0) {
    result.properties = { ...result.properties, sdk_dropped_fields: sdkDroppedFields };
  }

  return result;
}
